/**
 * Group State Management Context
 * 
 * Centralized state management for the current group.
 * Provides actions and state to all components that need group data.
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Group, Participant, AddParticipantParams } from './groupModel';
import { groupStorage } from './storage';
import { addParticipant, removeParticipant, loadGroup, updateGroup } from './groupService';
import { validateAddParticipantParams } from './validation';
import { addExpense, CreateExpenseParams, calculateGroupBalances } from './expenseService';
import { extractGroupFromUrl, hasGroupDataInUrl, cleanUrl } from './shareLink';

// State interface
interface GroupState {
  currentGroup: Group | null;
  loading: boolean;
  error: string | null;
  isOnline: boolean;
}

// Action types
type GroupAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_GROUP'; payload: Group | null }
  | { type: 'UPDATE_GROUP'; payload: Group }
  | { type: 'SET_ONLINE'; payload: boolean }
  | { type: 'CLEAR_GROUP' };

// Initial state
const initialState: GroupState = {
  currentGroup: null,
  loading: false,
  error: null,
  isOnline: navigator.onLine
};

// Reducer
function groupReducer(state: GroupState, action: GroupAction): GroupState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_GROUP':
      return { 
        ...state, 
        currentGroup: action.payload, 
        loading: false, 
        error: null 
      };
    
    case 'UPDATE_GROUP':
      return { 
        ...state, 
        currentGroup: action.payload, 
        error: null 
      };
    
    case 'SET_ONLINE':
      return { ...state, isOnline: action.payload };
    
    case 'CLEAR_GROUP':
      return { 
        ...state, 
        currentGroup: null, 
        loading: false, 
        error: null 
      };
    
    default:
      return state;
  }
}

// Context interface
interface GroupContextType {
  state: GroupState;
  actions: {
    loadGroup: (groupId: string) => Promise<void>;
    addParticipant: (params: AddParticipantParams) => Promise<void>;
    removeParticipant: (participantId: string) => Promise<void>;
    updateGroupInfo: (updates: Partial<Pick<Group, 'name' | 'description' | 'defaultCurrency'>>) => Promise<void>;
    addExpense: (params: CreateExpenseParams) => Promise<void>;
    clearGroup: () => void;
    clearError: () => void;
  };
}

// Create context
const GroupContext = createContext<GroupContextType | undefined>(undefined);

// Provider component
interface GroupProviderProps {
  children: ReactNode;
}

export const GroupProvider: React.FC<GroupProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(groupReducer, initialState);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => dispatch({ type: 'SET_ONLINE', payload: true });
    const handleOffline = () => dispatch({ type: 'SET_ONLINE', payload: false });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Actions
  const actions = {
    loadGroup: async (groupId: string): Promise<void> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        // First, try to load from URL if data is present
        if (hasGroupDataInUrl()) {
          const groupFromUrl = extractGroupFromUrl();
          if (groupFromUrl && groupFromUrl.id === groupId) {
            // Recalculate balances
            const groupWithBalances = calculateGroupBalances(groupFromUrl);
            
            // Save to localStorage for future use
            groupStorage.saveGroup(groupWithBalances);
            groupStorage.setCurrentGroup(groupId);
            
            // Clean the URL for privacy/security
            cleanUrl();
            
            dispatch({ type: 'SET_GROUP', payload: groupWithBalances });
            return;
          }
        }

        // Fallback: try to load from localStorage
        const group = loadGroup(groupId);
        if (!group) {
          throw new Error('Group not found. The link may be invalid or expired.');
        }
        dispatch({ type: 'SET_GROUP', payload: group });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load group';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      }
    },

    addParticipant: async (params: AddParticipantParams): Promise<void> => {
      if (!state.currentGroup) {
        throw new Error('No group loaded');
      }

      try {
        // Validate input
        const validatedParams = validateAddParticipantParams(params);
        
        // Add participant
        const updatedGroup = addParticipant(state.currentGroup.id, validatedParams);
        if (!updatedGroup) {
          throw new Error('Failed to add participant');
        }
        
        dispatch({ type: 'UPDATE_GROUP', payload: updatedGroup });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to add participant';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw error; // Re-throw for component handling
      }
    },

    removeParticipant: async (participantId: string): Promise<void> => {
      if (!state.currentGroup) {
        throw new Error('No group loaded');
      }

      try {
        const updatedGroup = removeParticipant(state.currentGroup.id, participantId);
        if (!updatedGroup) {
          throw new Error('Failed to remove participant');
        }
        
        dispatch({ type: 'UPDATE_GROUP', payload: updatedGroup });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to remove participant';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw error; // Re-throw for component handling
      }
    },

    updateGroupInfo: async (updates: Partial<Pick<Group, 'name' | 'description' | 'defaultCurrency'>>): Promise<void> => {
      if (!state.currentGroup) {
        throw new Error('No group loaded');
      }

      try {
        const updatedGroup = updateGroup(state.currentGroup.id, updates);
        if (!updatedGroup) {
          throw new Error('Failed to update group');
        }
        
        dispatch({ type: 'UPDATE_GROUP', payload: updatedGroup });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update group';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw error; // Re-throw for component handling
      }
    },

    clearGroup: (): void => {
      dispatch({ type: 'CLEAR_GROUP' });
      groupStorage.clearCurrentGroup();
    },

    addExpense: async (params: CreateExpenseParams): Promise<void> => {
      if (!state.currentGroup) {
        throw new Error('No group loaded');
      }

      try {
        const updatedGroup = addExpense(state.currentGroup.id, params);
        if (!updatedGroup) {
          throw new Error('Failed to add expense');
        }
        
        dispatch({ type: 'UPDATE_GROUP', payload: updatedGroup });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to add expense';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw error; // Re-throw for component handling
      }
    },

    clearError: (): void => {
      dispatch({ type: 'SET_ERROR', payload: null });
    }
  };

  const contextValue: GroupContextType = {
    state,
    actions
  };

  return (
    <GroupContext.Provider value={contextValue}>
      {children}
    </GroupContext.Provider>
  );
};

// Hook to use group context
export const useGroup = (): GroupContextType => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within a GroupProvider');
  }
  return context;
};

// Convenience hooks for specific data
export const useCurrentGroup = (): Group | null => {
  const { state } = useGroup();
  return state.currentGroup;
};

export const useGroupParticipants = (): Participant[] => {
  const { state } = useGroup();
  return state.currentGroup?.participants || [];
};

export const useGroupLoading = (): boolean => {
  const { state } = useGroup();
  return state.loading;
};

export const useGroupError = (): string | null => {
  const { state } = useGroup();
  return state.error;
};
